<script lang="ts">
  export * from './Image.types';
</script>

<script setup lang="ts">
  import { computed, inject, useAttrs } from 'vue';

  import type { StashImageProviders, StashProvideState } from '../../../types/misc';
  import { SCREEN_SIZES } from '../../constants';
  import type { ImageRadii, ImageSizeCondition } from './Image.types';
  import { ImageProviders, ImageRadius, Screens } from './Image.types';
  import providers from './providers';
  import type { ImageModifiers } from './providers/utils';

  export interface ImageProps {
    /**
     * The path to the image you want to embed.
     */
    src: string;

    /**
     * Native srcset attribute.
     * One or more strings separated by commas, indicating possible image sources
     * Can only be used with provider=static, otherwise it's ignored and auto-generated with `sizes` usage
     */
    srcset?: string;

    /**
     * For specifying responsive sizes
     */
    sizes?: string;

    /**
     * Where the image is served from.
     * If not provided, the provider will inherit from the Stash config `stashOptions.images.provider` (default: `static`).
     *  - `static` for relative or absolute paths
     *  - `cloudinary` for images served via Cloudinary
     */
    provider?: StashImageProviders;

    /**
     * For applying border radius
     */
    radius?: ImageRadii;

    /**
     * A custom static path the image src will be appended onto when provider=static.
     * Can be used to override the library-level default.
     */
    staticPath?: string;

    /**
     * TODO - https://leaflink.atlassian.net/browse/GRO-204
     * A custom function used to resolve a URL string for the image
     */
    // loader?: () => string;
  }

  defineOptions({
    inheritAttrs: false,
  });

  const BREAKPOINTS = {
    md: SCREEN_SIZES.md,
    lg: SCREEN_SIZES.lg,
  };
  const stashOptions = inject<StashProvideState>('stashOptions');
  const props = withDefaults(defineProps<ImageProps>(), {
    srcset: undefined,
    sizes: undefined,
    staticPath: undefined,
    provider: undefined,
    radius: 'none',
    // loader: undefined, // TODO - https://leaflink.atlassian.net/browse/GRO-204
  });

  const attrs = computed(() => {
    const { src, ...attrs } = useAttrs();

    attrs.sizes = imgSizes.value;
    attrs.srcset = imgSrcset.value;

    return attrs;
  });

  const isAbsoluteUrl = computed(() => {
    // return true if not an absolute url
    try {
      new URL(props.src);
      return true;
    } catch (e) {
      return false;
    }
  });

  const computedProvider = computed(() => props.provider || stashOptions?.images?.provider || ImageProviders.Static);

  const computedStaticPath = computed(() => props.staticPath ?? stashOptions?.staticPath);

  const isStatic = computed(() => computedProvider.value === ImageProviders.Static);

  const imgProvider = computed(() => providers[computedProvider.value]);

  const imgSrc = computed(() => (isStatic.value ? getProviderImage() : getProviderImage({ width: Screens.md })));

  const imgSizes = computed(() => (props.sizes ? getSizes() : props.sizes));

  const imgSrcset = computed(() => (props.sizes && !props.srcset && !isStatic.value ? getSources() : props.srcset));

  const parsedSizes = computed(() => {
    return props.sizes ? parseSizes(props.sizes) : [];
  });

  function getProviderImage(modifiers: ImageModifiers = {}) {
    if (isStatic.value && isAbsoluteUrl.value) {
      return props.src;
    }

    const src = isStatic.value && computedStaticPath.value ? `${computedStaticPath.value}/${props.src}` : props.src;

    return imgProvider.value.getImageUrl(src, modifiers);
  }

  function getSources() {
    return Object.values(Screens)
      .map((width) => {
        const src = getProviderImage({ width });

        return `${src} ${width}w`;
      })
      .join(', ');
  }

  function getSizes() {
    // return if using native media conditions
    if (props.sizes?.includes('(')) {
      return props.sizes;
    }

    return parsedSizes.value.map((v) => `${v.mediaQuery ? v.mediaQuery + ' ' : ''}${v.size}`).join(', ');
  }

  function parseSizes(providedSizes = '') {
    const conditions: ImageSizeCondition[] = [];
    const sizes = {
      default: '100vw',
    };

    // parse sizes and convert to object
    if (typeof providedSizes === 'string') {
      const definitions = providedSizes.split(/[\s]+/).filter((size) => size);

      for (const entry of definitions) {
        const size = entry.split(':');

        if (size.length !== 2) {
          sizes['default'] = size[0].trim();
          continue;
        }

        sizes[size[0].trim()] = size[1].trim();
      }
    } else {
      throw new Error('`sizes` needs to be a string');
    }

    for (const key in sizes) {
      const screenMinWidth = parseInt(BREAKPOINTS[key] || 0);
      let size = String(sizes[key]);
      const isFluidSize = size.endsWith('vw');

      // convert integer to pixels
      if (!isFluidSize && /^\d+$/.test(size)) {
        size = `${size}px`;
      }

      if (!isFluidSize && size.endsWith('%')) {
        throw new Error('Image: `sizes` does not support percentage values');
      }

      const condition = {
        mediaQuery: screenMinWidth ? `(min-width: ${screenMinWidth}px)` : '',
        screenMinWidth,
        size,
      };

      conditions.push(condition);
    }

    conditions.sort((v1, v2) => (v1.screenMinWidth > v2.screenMinWidth ? -1 : 1));

    return conditions;
  }
</script>

<template>
  <img
    ref="img"
    :key="imgSrc"
    data-test="stash-image"
    class="stash-image"
    :class="{
      rounded: props.radius === ImageRadius.Rounded,
      'rounded-full': props.radius === ImageRadius.Circle,
    }"
    :src="imgSrc"
    v-bind="attrs"
  />
</template>
